import { ChangeDetectorRef, NgZone, OnDestroy, Pipe, PipeTransform } from '@angular/core';

/**
 * Relative-time formatting ("3 minutes ago").
 *
 * This replaces the `time-ago-pipe` package, which was compiled for ViewEngine and
 * only ever worked through ngcc. ngcc was removed in Angular 16, so the subclass we
 * used to declare (`TimeAgoExtendsPipe extends TimeAgoPipe`) inherited no DI
 * metadata and was constructed with undefined deps — every `transform` then threw
 * "Cannot read properties of undefined (reading 'runOutsideAngular')". Because the
 * pipe is impure that fired on every change-detection pass.
 *
 * Output strings and the self-refresh cadence match the original package.
 */
@Pipe({
    name: 'timeAgo',
    pure: false,
    standalone: false
})
export class TimeAgoPipe implements PipeTransform, OnDestroy {

  private timer: number | null;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone
  ) { }

  transform(value: any): string {
    this.removeTimer();

    let d = new Date(value);
    let now = new Date();
    let seconds = Math.round(Math.abs((now.getTime() - d.getTime()) / 1000));

    if (Number.isNaN(seconds)) {
      return '';
    }

    // Re-render the label as it goes stale, without waking change detection in between.
    let timeToUpdate = this.getSecondsUntilUpdate(seconds) * 1000;
    this.timer = this.ngZone.runOutsideAngular(() => {
      if (typeof window === 'undefined') {
        return null;
      }
      return window.setTimeout(() => {
        this.ngZone.run(() => this.changeDetectorRef.markForCheck());
      }, timeToUpdate);
    });

    let minutes = Math.round(Math.abs(seconds / 60));
    let hours = Math.round(Math.abs(minutes / 60));
    let days = Math.round(Math.abs(hours / 24));
    let months = Math.round(Math.abs(days / 30.416));
    let years = Math.round(Math.abs(days / 365));

    if (seconds <= 45) {
      return 'a few seconds ago';
    } else if (seconds <= 90) {
      return 'a minute ago';
    } else if (minutes <= 45) {
      return minutes + ' minutes ago';
    } else if (minutes <= 90) {
      return 'an hour ago';
    } else if (hours <= 22) {
      return hours + ' hours ago';
    } else if (hours <= 36) {
      return 'a day ago';
    } else if (days <= 25) {
      return days + ' days ago';
    } else if (days <= 45) {
      return 'a month ago';
    } else if (days <= 345) {
      return months + ' months ago';
    } else if (days <= 545) {
      return 'a year ago';
    } else {
      return years + ' years ago';
    }
  }

  ngOnDestroy(): void {
    this.removeTimer();
  }

  private removeTimer(): void {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private getSecondsUntilUpdate(seconds: number): number {
    let min = 60;
    let hr = min * 60;
    let day = hr * 24;

    if (seconds < min) {
      return 2;      // under a minute — refresh every 2s
    } else if (seconds < hr) {
      return 30;     // under an hour — every 30s
    } else if (seconds < day) {
      return 300;    // under a day — every 5 min
    } else {
      return 3600;   // otherwise hourly
    }
  }
}
